import Select, { MultiValue, SingleValue } from 'react-select'
import { t } from '../../i18n'
import { questionnaireCopy } from './copy'
import { needsText } from './model'
import { Answer, Question, ValidationErrors } from './types'

type Option = { value: number; label: string }

export function QuestionField({
  question,
  value,
  text,
  errors,
  disabled,
  onChange,
}: {
  question: Question
  value: Answer
  text: string
  errors: ValidationErrors
  disabled: boolean
  onChange: (name: string, value: Answer) => void
}) {
  const id = `question-${question.name}`
  const error = errors[question.name]
  const textName = `${question.name}_text`
  const textError = errors[textName]
  const options = question.choices.map((choice) => ({
    value: choice.value,
    label: choice.text,
  }))
  const textClassName = (invalid: boolean) =>
    `border-2 ${invalid ? 'border-red-500' : 'border-gray-300 dark:border-gray-700'} rounded-lg p-2 w-full bg-white dark:bg-gray-800 dark:text-white disabled:opacity-60`

  return (
    <div className="flex flex-col gap-2">
      <label htmlFor={id}>
        {question.text}
        {question.required && <span aria-hidden="true"> *</span>}
      </label>
      {question.type === 'string' ? (
        <textarea
          id={id}
          rows={4}
          value={typeof value === 'string' ? value : ''}
          className={textClassName(!!error)}
          disabled={disabled}
          aria-required={!!question.required}
          aria-invalid={!!error}
          aria-describedby={error ? `${id}-error` : undefined}
          onChange={(event) => onChange(question.name, event.target.value)}
        />
      ) : (
        <Select<Option, boolean>
          inputId={id}
          instanceId={id}
          unstyled
          isClearable
          isDisabled={disabled}
          isMulti={question.type === 'multiple'}
          aria-required={!!question.required}
          aria-invalid={!!error}
          aria-describedby={error ? `${id}-error` : undefined}
          placeholder={t('noSelection')}
          options={options}
          value={
            question.type === 'multiple'
              ? options.filter(
                  (option) =>
                    Array.isArray(value) && value.includes(option.value),
                )
              : options.find((option) => option.value === value) ?? null
          }
          classNames={{
            control: () =>
              `border-2 ${error ? 'border-red-500' : 'border-gray-300 dark:border-gray-700'} rounded-lg bg-white dark:bg-gray-800 dark:text-white`,
            placeholder: () => 'text-gray-500 dark:text-gray-400',
            valueContainer: () => 'flex items-center gap-2 p-2',
            singleValue: () => 'text-gray-900 dark:text-white',
            multiValue: () => 'bg-gray-200 dark:bg-gray-700 p-1 rounded',
            multiValueRemove: () =>
              'px-1 hover:bg-gray-300 dark:hover:bg-gray-600',
            indicatorsContainer: () => 'flex items-center gap-2 px-2',
            menu: () =>
              'bg-white dark:bg-gray-700 p-1 border rounded-lg shadow-lg z-10',
            option: ({ isFocused, isSelected }) =>
              `${isFocused ? 'bg-gray-100 dark:bg-gray-900' : ''} ${isSelected ? 'bg-gray-200 dark:bg-gray-800' : ''} text-gray-900 dark:text-white p-2 rounded cursor-pointer`,
          }}
          onChange={(selected, action) => {
            if (question.type === 'single') {
              onChange(
                question.name,
                (selected as SingleValue<Option>)?.value ?? null,
              )
              return
            }
            let values = (selected as MultiValue<Option>).map(
              (option) => option.value,
            )
            if (action.action === 'select-option' && action.option) {
              const chosen = question.choices.find(
                (choice) => choice.value === action.option!.value,
              )
              values = chosen?.exclusive
                ? [chosen.value]
                : values.filter(
                    (code) =>
                      !question.choices.some(
                        (choice) => choice.value === code && choice.exclusive,
                      ),
                  )
            }
            onChange(question.name, values)
          }}
        />
      )}
      {error && (
        <p id={`${id}-error`} className="text-red-600 dark:text-red-400">
          {questionnaireCopy[error]}
        </p>
      )}
      {needsText(question, value) && (
        <>
          <label htmlFor={`${id}-text`}>
            {t('pleaseSpecify')}
            <span aria-hidden="true"> *</span>
          </label>
          <textarea
            id={`${id}-text`}
            rows={3}
            className={textClassName(!!textError)}
            value={text}
            disabled={disabled}
            aria-required="true"
            aria-invalid={!!textError}
            aria-describedby={textError ? `${id}-text-error` : undefined}
            onChange={(event) => onChange(textName, event.target.value)}
          />
          {textError && (
            <p
              id={`${id}-text-error`}
              className="text-red-600 dark:text-red-400"
            >
              {questionnaireCopy[textError]}
            </p>
          )}
        </>
      )}
    </div>
  )
}
